

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Loader2 } from 'lucide-react';
import { cn, getEventTiming } from '@/lib/utils';
import { authFetch } from '@/lib/auth-client';
import { asset } from '@/lib/asset';
import { LockCountdown } from '@/components/events/LockCountdown';

const SPORT_ICONS: Record<string, string> = {
  NBA: asset('assets/sports/basketball.webp'),
  NCAAB: asset('assets/sports/basketball.webp'),
  NCAA_BASKETBALL: asset('assets/sports/basketball.webp'),
  NHL: asset('assets/sports/hockey.webp'),
  NFL: asset('assets/sports/football.webp'),
  NCAA_FOOTBALL: asset('assets/sports/football.webp'),
  NCAAF: asset('assets/sports/football.webp'),
  MLB: asset('assets/sports/baseball.webp'),
  SOCCER: asset('assets/sports/soccer.webp'),
  SOCCER_EPL: asset('assets/sports/soccer.webp'),
  SOCCER_WORLD_CUP: asset('assets/sports/soccer.webp'),
  SOCCER_UCL: asset('assets/sports/soccer.webp'),
  MLS: asset('assets/sports/soccer.webp'),
  TENNIS: asset('assets/sports/tennis.webp'),
  GOLF: asset('assets/sports/golf.webp'),
  MMA: asset('assets/sports/mma.webp'),
  F1: asset('assets/sports/f1.webp'),
  ESPORTS: asset('assets/sports/esports.webp'),
  HORSE_RACING: asset('assets/sports/horse-racing.webp'),
};

const STATUS_COLORS: Record<string, { accent: string; bg: string; border: string }> = {
  UPCOMING: { accent: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
  OPEN: { accent: 'text-verdigris', bg: 'bg-verdigris/10', border: 'border-verdigris/30' },
  LOCKED: { accent: 'text-gold', bg: 'bg-gold/10', border: 'border-gold/30' },
  COMPLETED: { accent: 'text-tertiary', bg: 'bg-tertiary/10', border: 'border-tertiary/30' },
};

interface MyEvent {
  entryId: string;
  mainEventId: string;
  title: string;
  sport: string;
  status: string;
  startTime: string;
  endTime: string;
  prizePool: number;
  totalScore: number;
  propScore: number;
  rank: number | null;
  undercardsEntered: number;
  nextLockAt: string | null;
}

export function MyEvents() {
  const [events, setEvents] = useState<MyEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchEvents() {
      try {
        const res = await authFetch('/api/main-events/my-events');
        if (res.ok) {
          const data = await res.json();
          setEvents(data.events || []);
        }
      } catch (error) {
        console.error('Failed to fetch my events:', error);
      } finally {
        setIsLoading(false);
      }
    }
    fetchEvents();
  }, []);

  if (isLoading) {
    return (
      <div className="py-8 flex justify-center">
        <Loader2 className="h-6 w-6 text-gold animate-spin" />
      </div>
    );
  }

  const currentEvents = events.filter((e) =>
    ['UPCOMING', 'OPEN', 'LOCKED'].includes(e.status)
  );
  const pastEvents = events.filter((e) => e.status === 'COMPLETED');

  return (
    <div className="space-y-6">
      <EventSection
        title="Current Events"
        icon={asset('assets/icons/live-event.webp')}
        events={currentEvents}
        emptyMessage="You haven't entered any active events"
      />
      <EventSection
        title="Past Events"
        icon={asset('assets/icons/trophy.webp')}
        events={pastEvents}
        emptyMessage="No completed events yet"
      />
    </div>
  );
}

function EventSection({
  title,
  icon,
  events,
  emptyMessage,
}: {
  title: string;
  icon: string;
  events: MyEvent[];
  emptyMessage: string;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Image src={icon} alt="" width={20} height={20} unoptimized className="object-contain" />
        <span className="text-gold text-xs font-bold tracking-[0.2em] uppercase">
          {title}
        </span>
      </div>

      {events.length === 0 ? (
        <div className="rounded-xl bg-surface-2 border border-surface-3 p-6 text-center">
          <p className="text-warm-gray text-sm">{emptyMessage}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map((event) => (
            <EventCard key={event.entryId} event={event} />
          ))}
        </div>
      )}
    </div>
  );
}

function EventCard({ event }: { event: MyEvent }) {
  const sportIcon = SPORT_ICONS[event.sport] || asset('assets/sports/basketball.webp');
  const statusConfig = STATUS_COLORS[event.status] || STATUS_COLORS.UPCOMING;
  const timing = getEventTiming(event.startTime, event.endTime);
  const isCompleted = event.status === 'COMPLETED';
  const fallbackTiming = (
    <span className={cn('flex items-center gap-1', timing.isLive && 'text-verdigris font-medium')}>
      <Image src={asset('assets/icons/pocket_watch.webp')} alt="" width={14} height={14} />
      <span className="truncate">{timing.label}</span>
    </span>
  );

  return (
    <Link href={`/events/main/${event.mainEventId}`}>
      <div className="rounded-xl bg-surface-2 border border-surface-3 hover:border-surface-4 transition-all duration-200 hover:-translate-y-0.5 cursor-pointer p-3 sm:p-4">
        <div className="flex items-center gap-3 sm:gap-4">
          {/* Sport icon */}
          <Image
            src={sportIcon}
            alt={event.sport}
            width={40}
            height={40}
            className="w-9 h-9 sm:w-10 sm:h-10 object-contain opacity-80 flex-shrink-0"
          />

          {/* Event info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="font-serif font-bold text-cream truncate text-sm sm:text-base min-w-0">{event.title}</h3>
              <span
                className={cn(
                  'px-2 py-0.5 rounded text-[9px] sm:text-[10px] font-bold uppercase tracking-wider border flex-shrink-0',
                  statusConfig.bg,
                  statusConfig.border,
                  statusConfig.accent
                )}
              >
                {event.status}
              </span>
            </div>

            <div className="flex items-center gap-x-2 sm:gap-x-4 gap-y-1 text-[10px] sm:text-xs text-tertiary flex-wrap">
              <span>{event.sport}</span>
              <span>{event.undercardsEntered} rounds</span>
              {!isCompleted && event.nextLockAt ? (
                <LockCountdown deadline={event.nextLockAt} label="Round locks in" fallback={fallbackTiming} />
              ) : (
                fallbackTiming
              )}
            </div>
          </div>

          {/* Score / Rank */}
          <div className="flex-shrink-0 text-right">
            {isCompleted && event.rank ? (
              <div>
                <p className="text-[10px] sm:text-xs text-tertiary">Rank</p>
                <p className="text-base sm:text-lg font-serif font-bold text-gold">#{event.rank}</p>
              </div>
            ) : (
              <div>
                <p className="text-[10px] sm:text-xs text-tertiary">Score</p>
                <p className="text-base sm:text-lg font-serif font-bold text-cream">{event.totalScore}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
